import { Component, inject, Inject, OnInit } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DocusignService } from '@lmc-app/core/services/docusign.service';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { finalize } from 'rxjs';
import { Loading } from '@lmc-app/applications/loading';
import { TEMPLATE_NAMES, APPLICANT_TYPE, ADDITIONAL_DELIVERY_OPTIONS } from '../constants/docusign-constants';
import { mergeDeliveryOptions, addAdditionalDelivery, callLoader } from '../helper-functions/docusign-utils';
import { ListenerService } from '@lmc-app/core/services/listener.service';

@Component({
  selector: 'app-preview-docusign-modal',
  templateUrl: './preview-docusign-modal.component.html',
  styles: [
    ` 
      .modal-container {
        height: 80vh; 
        overflow-y: auto; 
      }
      
      .container-fluid{
        padding: 0;
      }

      .modal-header {
        position: sticky;
        top: 0; /* Sticks to the top of its scrolling container */
        background: white; /* Add a background to prevent content from showing through */
        z-index: 10; 
        display: flex; 
        justify-content: space-between; 
        width: 100%; background-color: #f7f7f7; 
        // margin-bottom: 1rem;
      }

      .mat-success {
        background-color: green;
        color: #fff;
      }

      .mat-success[disabled] {
        background-color: rgba(0, 0, 0, 0.12) !important; /* Standard Material gray */
        color: white;
        cursor: not-allowed;
      }
      
      h5 {
        font-size: 16px;
        font-weight: bold;
      }

      /* Recipient status panel beside the PDF once the envelope is sent */
      /* Fixed height so the recipient list scrolls instead of stretching the modal */
      .modal-body--with-status {
        display: flex;
        gap: 0;
        padding: 0;
        height: 75vh;
      }

      .modal-body--with-status .preview-pdf {
        flex: 1 1 auto;
        min-width: 0;
        height: 100%;
        overflow: auto;
      }

      .preview-status {
        flex: 0 0 400px;
        display: flex;
        flex-direction: column;
        min-height: 0;
        height: 100%;
        border-left: 1px solid #e5e5ea;
        border-top: 6px solid #4c6fd9;
        background: #f5f5f7;   /* same grey as the recipient status page, so the empty space below matches */
      }

      .preview-status__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 12px 4px 16px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #6b6b76;   /* same muted label style as "Send to" */
      }

      /* Same as .action-icon in the Agreements preview */
      .preview-status__refresh {
        cursor: pointer;
        color: #54575a;
      }

      .preview-status__body {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
      }

      /* Header: template + owner on the left, actions on the right */
      .preview-header {
        align-items: center;
        padding: 10px 16px;
        background-color: #fff;
        border-bottom: 1px solid #e5e5ea;
        cursor: move;
      }

      .preview-header__title h5 {
        margin: 0;
        letter-spacing: 0.3px;
      }

      .preview-header__owner {
        font-size: 13px;
        color: #6b6b76;
      }

      .preview-header__actions {
        display: flex;
        gap: 8px;
        cursor: default;
      }

      /* Draft only: who the envelope will go to */
      .preview-send-to {
        display: flex;
        align-items: center;
        padding: 8px 16px;
        background: #f5f7fb;
        border-bottom: 1px solid #e5e5ea;
        font-size: 13px;
      }

      .preview-send-to > div {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
      }

      .preview-send-to__label {
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #6b6b76;
      }

      .preview-send-to__recipient {
        display: inline-flex;
        align-items: center;
        gap: 10px;
      }

      .preview-send-to__recipient span {
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }

      .preview-send-to__recipient mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
        color: #6b6b76;
      }

      .preview-send-to__role {
        padding: 2px 8px;
        border-radius: 10px;
        background: #e8eef8;
        color: #4c6fd9;
        font-size: 11px;
        font-weight: bold;
      }

      /* Envelope status legend (same as the Agreements preview) */
      .status-legend {
        display: flex;
        align-items: center;
        gap: 12px;
        justify-content: flex-end;
        font-size: smaller;
        color: #5b5b66;
        padding: 4px 16px;
        background: #fff;
      }

      .legend-item {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-weight: 600;
        color: #000000;
      }

      .legend-item--inactive {
        opacity: 0.35;
        font-weight: 400;
        color: inherit;
      }

      .legend-dot {
        flex-shrink: 0;
        width: 8px;
        height: 8px;
        border-radius: 999px;
      }

      .legend-dot--draft { background: #c2c2c9; } /* Neutral Gray */
      .legend-dot--sent { background: #4c6fd9; } /* Info Blue */
      .legend-dot--void { background: #d64545; } /* Alert Red */
      .legend-dot--completed { background: #2fa84f; } /* Success Green */

      /* Recipient panel top border follows the envelope status */
      .preview-status--draft { border-top-color: #c2c2c9; }
      .preview-status--sent { border-top-color: #4c6fd9; }
      .preview-status--void { border-top-color: #d64545; }
      .preview-status--completed { border-top-color: #2fa84f; }
    `
  ]
})
export class PreviewDocusignModalComponent implements OnInit {
  private readonly _bottomSheet = inject(MatBottomSheet);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _listenerService = inject(ListenerService);
  
  pdfSource = null  //: Uint8Array | null = null;

  currentUser:any;
  templateName: string;
  displayTemplateName: string;
  sendTo: string;
  sendToCoApp: string;
  envelopeOwnerName: string;
  applicantType: string;
  isNotDraft: boolean = false;
  primaryPhoneNumber: string;
  coAppPhoneNumber: string;
  
  readonly _templateNames = TEMPLATE_NAMES;
  readonly _applicantTypes = APPLICANT_TYPE;
  readonly _deliveryOptions = ADDITIONAL_DELIVERY_OPTIONS;

  
  selectedDeliveryOption: string | null = null;
  additionalDeliveryObject: any;
  defaultDeliveryEmail = true;
  optionalDelivery: 'SMS' | 'WhatsApp' = 'SMS';
  subTableDetails: any;
  primaryPhone:string;
  coAppPhone: string;
  constructor(
      private _docusignService: DocusignService,
      public _dialogDocusignPreview: MatDialogRef<PreviewDocusignModalComponent>,
      @Inject(MAT_DIALOG_DATA) public data
  ){}

  ngOnInit(): void {
    this.currentUser = this.data?.userInfo;
    this.appData = this.mapZohoAppData(this.data?.application);
    console.log(this.data,"Open Data+++++")

    if(!!this.data?.envelopeData?.jsonData){
      const displayInfo = this.data?.envelopeData?.jsonData;
      this.templateName = displayInfo?.selectedTemplate?.templateType;
      this.displayTemplateName = displayInfo?.selectedTemplate?.templateType?.replaceAll('_', ' ');
      this.sendTo = displayInfo?.envelopeRecipients?.[0]?.email;
      this.sendToCoApp = displayInfo?.dbSubEnvelopeDetails?.co_applicant_email;
      this.envelopeOwnerName = displayInfo?.envelopeRecipients?.[0]?.name;
      this.applicantType = displayInfo?.dbMainEnvelope?.applicant_type;
      // this.isNotDraft = displayInfo?.docusign_res?.status?.toLowerCase() === 'created';
      this.isNotDraft = ['created']?.includes(displayInfo?.docusign_res?.status?.toLowerCase());
      this.openEnvelope(displayInfo?.docusign_res?.envelope_id);
      this.subTableDetails = displayInfo?.dbSubEnvelopeDetails;
      this.primaryPhoneNumber = displayInfo?.dbSubEnvelopeDetails?.main_applicant_phone;
      this.coAppPhoneNumber = displayInfo?.dbSubEnvelopeDetails?.co_applicant_phone;
      this.envelopeId = displayInfo?.docusign_res?.envelope_id;
      this.statusTone = this.mapStatusTone(displayInfo?.docusign_res?.status);
    }else{
      // console.log(this.data?.envelopeData,"+++++")
      const envelopeOwnerName = (this.data?.envelopeData?.templateRowId?.template_name?.toLowerCase() === this._templateNames.CONSENT)
                                ? this.data?.envelopeData?.name
                                : this.data?.envelopeData?.applicant_type?.toLowerCase() === this._applicantTypes?.PRIMARY
                                ? this.data?.envelopeData?.envelopeDetails?.main_applicant_name
                                : this.data?.envelopeData?.envelopeDetails?.co_applicant_firstname?.concat(' ')?.concat(this.data?.envelopeData?.envelopeDetails?.co_applicant_lastname);

      this.templateName = this.data?.envelopeData?.templateRowId?.template_name;
      this.displayTemplateName = this.data?.envelopeData?.templateRowId?.template_name?.replaceAll('_', ' ');
      this.sendTo = this.data?.envelopeData?.client_email;
      this.sendToCoApp = this.data?.envelopeData?.envelopeDetails?.co_applicant_email;
      this.envelopeOwnerName = envelopeOwnerName?.toUpperCase();
      this.applicantType = this.data?.envelopeData?.applicant_type;
      // // this.isNotDraft = this.data?.envelopeData?.statusId?.status_name?.toLowerCase() === 'created';
      this.isNotDraft = ['created']?.includes(this.data?.envelopeData?.statusId?.status_name?.toLowerCase());
      this.openEnvelope(this.data?.envelopeData?.envelope_id);
      this.subTableDetails = this.data?.envelopeData?.envelopeDetails;
      this.primaryPhoneNumber = this.data?.envelopeData?.envelopeDetails?.main_applicant_phone;
      this.coAppPhoneNumber = this.data?.envelopeData?.envelopeDetails?.co_applicant_phone;
      this.envelopeId = this.data?.envelopeData?.envelope_id;
      this.statusTone = this.mapStatusTone(this.data?.envelopeData?.statusId?.status_name);
    }
  }  

  additionalDeliveryOption(option: string): void {
    this.selectedDeliveryOption = this.selectedDeliveryOption === option ? null : option;
    this.displayPhoneNumbers(this.selectedDeliveryOption);
  }
  
  displayPhoneNumbers(optionSelected:string){
    if(!!optionSelected){      
      this.primaryPhone = this.subTableDetails?.main_applicant_phone;
      this.coAppPhone = this.subTableDetails?.co_applicant_phone;
    } else{
      this.primaryPhone = null;
      this.coAppPhone = null;
    }
  }

  mergeDeliveryOptions(defaultOption:boolean,optional:string){
    return (defaultOption) ? 'Email'?.concat('And').concat(optional) : optional;
  }
  
  createDeliveryObject(phoneNumber:string, defaultOption:boolean,optional:string,recipientId:string){
    const deliveryOptionDetails = {
      secondaryDeliveryMethod: mergeDeliveryOptions(defaultOption,optional),
      phoneNumber: {
            countryCode: "1",
            number: phoneNumber
      }
    } 
    return deliveryOptionDetails;
  }  

  openEnvelope(envelopeId: string){
    this._docusignService.streamEnvelope(envelopeId).subscribe({
      next: (blob: Blob) => {
        const reader = new FileReader();
        reader.onload = () => {
          this.pdfSource = blob;  // new Uint8Array(reader.result as ArrayBuffer);
        };
        reader.readAsArrayBuffer(blob);
      },
      error: (err) => console.error("PDF Load Error:", err)
    });
  }

  onSend(){    
    if(!!this.data?.envelopeData?.jsonData){
      console.log("Send after creation");
      const isEdited = false;
      const addEvelopeDetails = {...this.data?.envelopeData?.jsonData, ...{isEdited: isEdited}};
      // this.data.envelopeData.jsonData.docusign_res.statusId.status_name = this.data?.envelopeData?.jsonData?.docusign_res?.status;
      const createdEnvelope = {
        envelope_id: this.data?.envelopeData?.jsonData?.docusign_res?.envelope_id, 
        envelopeStatus: this.data?.envelopeData?.jsonData?.docusign_res?.status, 
        evelopeDetails: addEvelopeDetails,
        // evelopeDetails: this.data?.envelopeData?.jsonData
      };
      this.sendEnvelope(createdEnvelope);
    }else{   
      console.log("Send row selected");   
      const isEdited = !!this.data?.envelopeData?.modified_at;
      const addEvelopeDetails = {...this.data?.envelopeData?.jsonData, ...{isEdited: isEdited}};
      const createdEnvelope = {
        envelope_id: this.data?.envelopeData?.envelope_id, 
        envelopeStatus: this.data?.envelopeData?.['statusId']?.['status_name'], 
        evelopeDetails: addEvelopeDetails
      };      
      this.sendEnvelope(createdEnvelope);
      // this.sendEnvelope(this.data?.envelopeData);
    }
  }

  sendEnvelope(envelopeData:any){
    const ref = callLoader(this._bottomSheet, 'Sending envelope')
    this._docusignService.sendEnvelope(
      {
        envelope_id: envelopeData?.envelope_id, 
        envelopeStatus: envelopeData?.envelopeStatus, 
        envelopeData: envelopeData?.evelopeDetails, 
        envelopeIsEdited: envelopeData?.isEdited, 
        userInfo: this.currentUser
      }
    ).pipe(
      finalize(() => {this._bottomSheet.dismiss(envelopeData);})
    ).subscribe({
      next: (response)=>{
        if(response?.status === 200){
          this.openSnackBar('success',response?.message);
          this._dialogDocusignPreview.close();
        } else{
          this.openSnackBar('error',`Send error:  ${response?.message}`);
        }
      }
    })
  }

  openSnackBar(messageType: string, message: string){
      if(messageType === 'error'){
        return this._snackBar.open(message, SnackbarConstants.ACTION_DISMISS,SnackbarConstants.DURATION_DISMISS_ALIVE,);
      } else{
        return this._snackBar.open(message, SnackbarConstants.ACTION_DISMISS,SnackbarConstants.DURATION_DISMISS_SHORT,);
      }
    }

  // Recipient status panel (shown once the envelope is past Draft)
  envelopeId: string;

  get showRecipientStatus(): boolean {
    return !!this.envelopeId && !this.isNotDraft;   // isNotDraft is true while the envelope is still a draft (created)
  }

  refreshRecipientStatus(): void {
    this._listenerService.emit('recipientStatus', true);
  }

  // ---------- Zoho sync ----------
  // Recipient corrections also update Zoho, so map the Deal into the same { value, field } shape
  // the Agreements data model uses (Client -> Email/Mobile, Co-Applicant -> CoAppEmail/CoAppMobile)
  appData: any;

  mapZohoAppData(application: any): any {
    if (!application) return null;
    return {
      ApplicationId: { value: application?.id },
      ContactId: { value: application?.Contact_Name?.id },
      Email: { value: application?.Email1, field: 'Email1' },
      Mobile: { value: application?.Mobile, field: 'Mobile' },
      CoAppEmail: { value: application?.Email, field: 'Email' },
      CoAppMobile: { value: application?.Secondary_Cell_Number, field: 'Secondary_Cell_Number' },
    };
  }

  // ---------- Status legend ----------
  // Same mapping as the Agreements preview; anything past Draft that isn't voided/completed counts as Sent
  statusTone: 'draft' | 'sent' | 'void' | 'completed' = 'draft';
  readonly statusToneMap: Record<string, 'draft' | 'sent' | 'void' | 'completed'> = {
    created: 'draft',
    completed: 'completed',
    voided: 'void',
  };

  mapStatusTone(statusName: string): 'draft' | 'sent' | 'void' | 'completed' {
    return this.statusToneMap[(statusName || '').toLowerCase()] ?? 'sent';
  }
}
